import React from "react";
import { Card, Typography, Box } from "@mui/material";
import { motion } from "framer-motion";
import { Feature } from "api/Accounts/AccountsInterfaces";
import { RectangularTextButton } from "components/Base/Buttons";

interface AccountTypeCardProps {
  id: number;
  name: string;
  priceMonthly: number;
  description: string | null;
  features: Feature[];
  selected: boolean;
  onSelect: () => void;
  includeCallToActionButton?: boolean;
}

export const AccountTypeCard: React.FC<AccountTypeCardProps> = ({
  id,
  name,
  priceMonthly,
  description,
  features,
  selected,
  onSelect,
  includeCallToActionButton = true,
}) => {
  return (
    <motion.div
      whileHover={{ y: -6, scale: 1.02 }}
      transition={{ type: "spring", stiffness: 220, damping: 18 }}
      style={{ width: "100%", height: "100%" }}
    >
      <Card
        onClick={onSelect}
        sx={{
          height: "100%",
          borderRadius: 5,
          p: { xs: 3, md: 4 },
          background: selected
            ? "linear-gradient(180deg, rgba(74,222,128,0.14) 0%, rgba(255,255,255,0.07) 100%)"
            : "rgba(255,255,255,0.06)",
          border: selected
            ? "1px solid rgba(74,222,128,0.45)"
            : "1px solid rgba(255,255,255,0.14)",
          backdropFilter: "blur(10px)",
          boxShadow: selected
            ? "0 20px 60px rgba(34,197,94,0.18)"
            : "0 20px 60px rgba(0,0,0,0.35)",
          position: "relative",
          overflow: "hidden",
          cursor: "pointer",
          color: "#fff",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <Box
          sx={{
            position: "absolute",
            right: -90,
            bottom: -90,
            width: 240,
            height: 240,
            borderRadius: "50%",
            background: selected
              ? "rgba(77, 222, 128, 0.42)"
              : "rgba(77, 222, 128, 0.22)",
            filter: "blur(2px)",
            pointerEvents: "none",
          }}
        />

        <Box
          sx={{
            position: "relative",
            zIndex: 1,
            display: "flex",
            flexDirection: "column",
            height: "100%",
          }}
        >
          <Typography
            variant="h5"
            sx={{
              color: "white",
              fontWeight: 900,
              mt: 1,
              mb: 1,
            }}
          >
            {name}
          </Typography>

          {priceMonthly >= 0 ? (
            <Typography
              variant="h4"
              sx={{
                mb: 2,
                fontWeight: 900,
                lineHeight: 1.1,
              }}
            >
              £{priceMonthly}
              <Typography
                component="span"
                sx={{
                  ml: 0.75,
                  fontSize: "2rem",
                  fontWeight: 500,
                }}
              >
                /mo
              </Typography>
            </Typography>
          ) : (
            <Typography
              variant="h4"
              sx={{
                mt: 2,
                mb: 2,
                fontSize: "1rem",
                fontWeight: 500,
                color: "rgba(255,255,255,0.72)",
              }}
            >
              Custom Pricing
            </Typography>
          )}

          {description && (
            <Typography
              sx={{
                color: "rgba(255,255,255,0.82)",
                mb: 3,
                lineHeight: 1.6,
              }}
            >
              {description}
            </Typography>
          )}

          {features?.length > 0 && (
            <Box
              component="ul"
              sx={{
                listStyle: "none",
                p: 0,
                m: 0,
                display: "grid",
                gap: 1.5,
                mb: 3,
              }}
            >
              {features.map((feature) => (
                <Box
                  component="li"
                  key={feature.id}
                  sx={{
                    display: "flex",
                    alignItems: "flex-start",
                    gap: 1.5,
                    p: 1.75,
                    borderRadius: 4,
                    background: "rgba(255,255,255,0.04)",
                    border: "1px solid rgba(255,255,255,0.10)",
                    transition: "all 0.2s ease",
                    "&:hover": {
                      background: "rgba(255,255,255,0.06)",
                      borderColor: "rgba(255,255,255,0.16)",
                    },
                  }}
                >
                  <Typography sx={{ variant: "h6", color: "rgba(255,255,255,0.86)" }}>
                    {feature.name}
                  </Typography>
                </Box>
              ))}
            </Box>
          )}

          <Box sx={{ mt: "auto" }}>
            {includeCallToActionButton && (
              <RectangularTextButton
                fullWidth
                colour={selected ? "highlighted" : "clear"}
                primaryColor="#65f48e"
                textColor={selected ? "#0f172a" : "#fff"}
                borderColor="rgba(255,255,255,0.24)"
                onClick={(e) => {
                  e.stopPropagation();
                  onSelect();
                }}
                sx={{
                  mt: 1,
                  py: 1.25,
                  borderRadius: 999,
                  fontWeight: 800,
                  textTransform: "none",
                  "&:hover": {
                    backgroundColor: selected ? "#4ade80" : "rgba(255,255,255,0.08)",
                    borderColor: "rgba(255,255,255,0.34)",
                  },
                }}
              >
                {selected ? "Selected" : "Choose Plan"}
              </RectangularTextButton>
            )}
          </Box>
        </Box>
      </Card>
    </motion.div>
  );
};